// OrgGraph — composition root for the interactive org chart.
//
// Owns:
//   * filter state (mode + selected department) + the OrgGraphFilters bar
//   * focused/centered-node state + click-to-recenter
//   * mobile detection (≤768 px) → degraded list view
//   * keyboard navigation between nodes (arrow keys)
//   * hover/focus PersonHoverCard wiring
//   * department palette (stable hash → muted hue)
//
// Does NOT own:
//   * data fetching — that's `<OrgGraphTab>`'s job.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import type { IOrgGraphResponse, IOrgGraphNode } from '@skystone/shared';
import { useForceSimulation, type NodePosition } from './hooks/useForceSimulation';
import { useGraphFilter, type FilterParams } from './hooks/useGraphFilter';
import { useZoomPan } from './hooks/useZoomPan';
import { OrgGraphFilters, type FilterMode } from './OrgGraphFilters';
import { PersonNode } from './PersonNode';
import { GraphGhostNode } from './GraphGhostNode';
import { EdgeLine } from './EdgeLine';
import { DepartmentHull } from './DepartmentHull';
import { PersonHoverCard } from '../people/PersonHoverCard';
import { augmentWithGhosts, isGhostId } from './ghosts';
import {
  ORPHAN_COLOR,
  ORPHAN_TEAM_ID,
  buildTeamColorMap,
  teamIdForNode,
} from './team-colors';
import { escapeAttributeValue, findByDataUpn } from '@/lib/svg-focus';

const MOBILE_BREAKPOINT_PX = 768;
/** Initial zoom applied after the simulation settles around the current
 *  user. Below 1× so larger graphs fit in the viewBox; the user can zoom
 *  in/out with the scroll wheel and pan with the mouse via the d3-zoom
 *  binding on the SVG root. Sub-team leader pods now sit 240 logical units
 *  away from their parent (was 80), so the overall extent is ~3× bigger
 *  than before — start zoomed out enough to see the spokes. */
const FOCUS_ZOOM_LEVEL = 0.5;
const PAN_AFTER_SETTLE_MS = 300;
const BREATHING_ALPHA_TARGET = 0.02;

interface OrgGraphProps {
  data: IOrgGraphResponse;
}

interface NodeAdjacency {
  managerId: string | null;
  reports: string[];
  /** Ids of all members of the same manager-group, INCLUDING this node,
   *  sorted alphabetically by displayName. Storing the full sorted list (not
   *  the "excluding self" list) lets `findAdjacentNode` locate this node's
   *  position deterministically via `indexOf(focusedId)` rather than a stale
   *  id-vs-displayName comparison. */
  siblingsSorted: string[];
}

/**
 * Inline mobile-breakpoint hook. Lives here because no shared `useMediaQuery`
 * exists and introducing one for a single caller is out of scope (see spec).
 */
function useIsMobile(breakpointPx = MOBILE_BREAKPOINT_PX): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (globalThis.window === undefined) return false;
    return globalThis.window.matchMedia(`(max-width: ${breakpointPx}px)`).matches;
  });

  useEffect(() => {
    if (globalThis.window === undefined) return undefined;
    const mq = globalThis.window.matchMedia(`(max-width: ${breakpointPx}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [breakpointPx]);

  return isMobile;
}

/**
 * Pre-compute manager / reports / siblings index keyed by node id so the
 * arrow-key navigation handler stays O(1) per keypress.
 */
function buildAdjacency(nodes: IOrgGraphNode[]): Map<string, NodeAdjacency> {
  // Ghost-manager bridges are non-interactive: they must never be a keyboard
  // target, nor a sibling of the real roots (which all share `managerUpn`
  // null with the ghosts). Excluding them here keeps `findAdjacentNode`
  // simple — only the ArrowUp-into-a-ghost case needs an explicit guard.
  const realNodes = nodes.filter((n) => !isGhostId(n.id));
  const byManager = new Map<string | null, IOrgGraphNode[]>();
  for (const node of realNodes) {
    const list = byManager.get(node.managerUpn) ?? [];
    list.push(node);
    byManager.set(node.managerUpn, list);
  }
  // Stable alphabetical sort within each sibling group so Left/Right is
  // predictable for keyboard users.
  for (const list of byManager.values()) {
    list.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  const adjacency = new Map<string, NodeAdjacency>();
  for (const node of realNodes) {
    const reports = (byManager.get(node.id) ?? []).map((n) => n.id);
    const siblingGroup = byManager.get(node.managerUpn) ?? [];
    const siblingsSorted = siblingGroup.map((n) => n.id);
    adjacency.set(node.id, {
      managerId: node.managerUpn,
      reports,
      siblingsSorted,
    });
  }
  return adjacency;
}

/**
 * Given the currently-focused id and a keyboard direction, returns the id of
 * the adjacent node the user should be moved to (or null if no such node).
 *
 * Sibling navigation walks the alphabetically-sorted sibling list (built in
 * `buildAdjacency`). The current node's position in that list is found by
 * `indexOf(focusedId)` — comparing IDs lexicographically would be wrong
 * because IDs and display names are unrelated in real data.
 */
/** Walk the alphabetically-sorted sibling list one step (wrapping at the ends).
 *  Returns null when the current node has no navigable siblings. */
function findSiblingNode(
  entry: NodeAdjacency,
  focusedId: string,
  key: 'ArrowLeft' | 'ArrowRight',
): string | null {
  const list = entry.siblingsSorted;
  if (list.length <= 1) return null;
  const currentIndex = list.indexOf(focusedId);
  if (currentIndex === -1) return null;
  if (key === 'ArrowLeft') {
    const prevIndex = currentIndex === 0 ? list.length - 1 : currentIndex - 1;
    return list[prevIndex];
  }
  const nextIndex = currentIndex === list.length - 1 ? 0 : currentIndex + 1;
  return list[nextIndex];
}

function findAdjacentNode(
  focusedId: string,
  key: string,
  adjacency: Map<string, NodeAdjacency>,
): string | null {
  const entry = adjacency.get(focusedId);
  if (!entry) return null;
  if (key === 'ArrowUp') {
    // A ghost manager is a non-interactive bridge, not a navigable node:
    // ArrowUp from someone reporting outside the export lands nowhere.
    if (entry.managerId !== null && isGhostId(entry.managerId)) return null;
    return entry.managerId;
  }
  if (key === 'ArrowDown') return entry.reports[0] ?? null;
  if (key === 'ArrowLeft' || key === 'ArrowRight') {
    return findSiblingNode(entry, focusedId, key);
  }
  return null;
}

interface MobileListViewProps {
  data: IOrgGraphResponse;
}

/** Mobile fallback — flat list grouped by department. */
function MobileListView({ data }: Readonly<MobileListViewProps>) {
  const grouped = useMemo(() => {
    const byDept = new Map<string, IOrgGraphNode[]>();
    for (const node of data.nodes) {
      const list = byDept.get(node.departmentId) ?? [];
      list.push(node);
      byDept.set(node.departmentId, list);
    }
    for (const list of byDept.values()) {
      list.sort((a, b) => a.displayName.localeCompare(b.displayName));
    }
    return byDept;
  }, [data.nodes]);

  return (
    <div className="space-y-4" data-testid="org-graph-mobile">
      <div className="rounded-md bg-muted/40 p-3 text-sm text-muted-foreground" role="note">
        Interactive chart available on desktop.
      </div>
      {data.departments.map((dept) => {
        const members = grouped.get(dept.id) ?? [];
        return (
          <details key={dept.id} className="rounded-md border border-border" open>
            <summary className="cursor-pointer select-none px-3 py-2 text-sm font-semibold text-foreground">
              {dept.name} ({members.length})
            </summary>
            <ul className="divide-y divide-border">
              {members.map((person) => (
                <li key={person.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <span
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground"
                    aria-hidden="true"
                  >
                    {initialsFromName(person.displayName)}
                  </span>
                  <span className="flex-1">
                    <span className="block font-medium text-foreground">{person.displayName}</span>
                    {person.jobTitle !== null && (
                      <span className="block text-xs text-muted-foreground">{person.jobTitle}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        );
      })}
    </div>
  );
}

/** Tiny helper used only by the mobile list (PersonNode's initialsOf takes a
 *  node, not a string, so duplicating one line here is cheaper than threading
 *  a wrapper). */
function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts.at(-1)![0]).toUpperCase();
}

interface DesktopGraphProps {
  data: IOrgGraphResponse;
}

/**
 * Desktop SVG composition. Split out so OrgGraph itself stays focused on the
 * mobile-vs-desktop fork; this keeps cognitive complexity per function low
 * (SonarCloud S3776).
 */
function DesktopGraph({ data }: Readonly<DesktopGraphProps>) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const { currentUserUpn } = data;

  // Filter state owned here; the bar below is purely presentational.
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null);

  const filterParams: FilterParams = useMemo(() => {
    if (filterMode === 'my-team') {
      return { mode: 'my-team', currentUserUpn };
    }
    if (filterMode === 'department' && selectedDepartmentId !== null) {
      return { mode: 'department', departmentId: selectedDepartmentId };
    }
    return { mode: 'all' };
  }, [filterMode, selectedDepartmentId, currentUserUpn]);

  const filtered = useGraphFilter(data, filterParams);
  const { departments } = filtered;

  // Augment the filtered roster with ghost-manager bridge nodes + dashed
  // ghost→person edges for anyone whose manager sits outside the export. Done
  // AFTER filtering so ghosts reflect only the visible roster. The simulation,
  // adjacency, hulls and rendering all run on this augmented set; colouring
  // (below) derives from the ORIGINAL roster so ghosts never take a palette
  // slot. See PRODOPSAI-484.
  const augmented = useMemo(
    () => augmentWithGhosts(filtered.nodes, filtered.edges),
    [filtered],
  );
  const { nodes, edges } = augmented;

  const handleModeChange = useCallback(
    (mode: FilterMode) => {
      setFilterMode(mode);
      // Pre-select the first department (alphabetical) the first time the user
      // switches to 'department' mode, so the dropdown isn't shown empty and
      // the filter actually applies immediately.
      if (mode === 'department' && selectedDepartmentId === null && data.departments.length > 0) {
        const firstDept = [...data.departments].sort((a, b) => a.name.localeCompare(b.name))[0];
        setSelectedDepartmentId(firstDept.id);
      }
    },
    [data.departments, selectedDepartmentId],
  );

  // Centre node: starts at current user (if any), updates on click.
  const [centerNodeId, setCenterNodeId] = useState<string | null>(currentUserUpn);
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(currentUserUpn);

  const { positions, isSettled } = useForceSimulation(nodes, edges, centerNodeId);
  const { transform, panTo, zoomTo } = useZoomPan(svgRef);

  const adjacency = useMemo(() => buildAdjacency(nodes), [nodes]);
  const departmentNameById = useMemo(
    () => Object.fromEntries(departments.map((d) => [d.id, d.name])),
    [departments],
  );

  // Team-colour wiring: a node's colour = the team it belongs to (= its
  // manager's team, or itself for roots). A manager's HULL colour = the
  // team they LEAD (which is keyed by their own id). The two lookups differ,
  // so a manager pops visually inside their own pod.
  //
  // Orphans (no manager, no reports) bypass the palette entirely and use a
  // muted neutral colour so they read as "not really part of a team".
  //
  // `hasReports`, the palette and the hulls all derive from the ORIGINAL
  // (pre-ghost) roster so synthesised ghosts never take a palette slot or get
  // a hull — mirroring how the Org Tree colours from the un-augmented data.
  const hasReports = useMemo(() => {
    const set = new Set<string>();
    for (const edge of filtered.edges) set.add(edge.source);
    return set;
  }, [filtered.edges]);
  const teamColorMap = useMemo(
    () => buildTeamColorMap(filtered.nodes, hasReports),
    [filtered.nodes, hasReports],
  );
  const colorForNode = useCallback(
    (node: IOrgGraphNode) => {
      // Ghost-manager bridges are always grey — they're not a team.
      if (isGhostId(node.id)) return ORPHAN_COLOR;
      const teamId = teamIdForNode(node, hasReports);
      if (teamId === ORPHAN_TEAM_ID) return ORPHAN_COLOR;
      return teamColorMap.get(teamId) ?? 'var(--color-muted)';
    },
    [teamColorMap, hasReports],
  );
  const colorForTeamLeader = useCallback(
    (leaderId: string) => teamColorMap.get(leaderId) ?? 'var(--color-muted)',
    [teamColorMap],
  );

  // Team leaders: anyone with at least one direct report in the visible
  // (filtered) graph. Sorted alphabetically by name so the hull layer
  // renders deterministically for snapshot-style tests. Orphans are not
  // team leaders by definition.
  const teamLeaders = useMemo(
    () =>
      nodes
        .filter((n) => hasReports.has(n.id))
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [nodes, hasReports],
  );

  // After the simulation settles around the centered node, ease viewport to
  // it and apply the initial zoom. Re-runs whenever the centred id changes
  // (click-to-recenter). The pan target compensates for the zoom level so
  // the focused node lands on the viewBox origin — the transformed content
  // ends up at `pos.x * k + tx` so we set `tx = -pos.x * k`.
  useEffect(() => {
    if (!isSettled || centerNodeId === null) return undefined;
    const pos = positions.get(centerNodeId);
    if (!pos) return undefined;
    const timer = setTimeout(() => {
      zoomTo(FOCUS_ZOOM_LEVEL);
      panTo(-pos.x * FOCUS_ZOOM_LEVEL, -pos.y * FOCUS_ZOOM_LEVEL);
    }, PAN_AFTER_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [centerNodeId, isSettled, positions, panTo, zoomTo]);

  const handleNodeClick = useCallback((nodeId: string) => {
    setCenterNodeId(nodeId);
    setFocusedNodeId(nodeId);
  }, []);

  const handleNodeKeyDown = useCallback(
    (event: KeyboardEvent<SVGGElement>, nodeId: string) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleNodeClick(nodeId);
        return;
      }
      const target = findAdjacentNode(nodeId, event.key, adjacency);
      if (target !== null) {
        event.preventDefault();
        setFocusedNodeId(target);
        // Move actual DOM focus so subsequent arrow keys originate from the
        // newly-focused node, not the stale one whose handler ran this event.
        // We locate the new <g> via its data-upn marker on the inner
        // PersonNode rendering — the data attribute is stable across renders.
        if (globalThis.window !== undefined) {
          // Defer to next microtask so React has flushed the data-focused
          // attribute change before we move focus.
          globalThis.window.requestAnimationFrame(() => {
            const root = svgRef.current;
            if (!root) return;
            // CSS.escape is part of the CSSOM and not guaranteed to exist even
            // when `window` does (older webviews, JSDOM in certain modes). Fall
            // back to attribute-iteration when it's missing so keyboard focus
            // still works instead of throwing.
            const escapedTarget = escapeAttributeValue(target);
            const inner = escapedTarget === null
              ? findByDataUpn(root, target)
              : root.querySelector(`[data-upn="${escapedTarget}"]`);
            const group = inner?.parentElement as unknown as SVGGElement | undefined;
            if (group && typeof group.focus === 'function') {
              group.focus();
            }
          });
        }
      }
    },
    [adjacency, handleNodeClick],
  );

  // Ghost-manager bridges are not real people — exclude them from the count
  // surfaced to assistive tech.
  const peopleCount = nodes.filter((n) => !isGhostId(n.id)).length;
  const ariaLabel = `Organisation chart, ${peopleCount} people across ${departments.length} departments`;

  return (
    <div className="space-y-3" data-testid="org-graph-desktop">
      <OrgGraphFilters
        mode={filterMode}
        departments={data.departments}
        currentUserUpn={currentUserUpn}
        selectedDepartmentId={selectedDepartmentId}
        onModeChange={handleModeChange}
        onDepartmentChange={setSelectedDepartmentId}
      />
      {filtered.fallbackReason === 'no-current-user' && (
        <div
          className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground"
          role="note"
          data-testid="org-graph-fallback-banner"
        >
          Showing all people — you&apos;re not in the Entra directory yet.
        </div>
      )}
      <div className="relative h-[640px] w-full overflow-hidden rounded-lg border border-border bg-gradient-to-b from-background to-muted/30">
      <svg
        ref={svgRef}
        role="img"
        aria-label={ariaLabel}
        className="h-full w-full cursor-grab active:cursor-grabbing"
        data-testid="org-graph-svg"
        viewBox="-500 -400 1000 800"
      >
        {/* Single <g> consumes the d3-zoom transform so mouse-drag pans and
            scroll-wheel zooms actually move the content. Without this
            wrapper the transform updates state but nothing on screen
            changes — historical bug, fixed in PRODOPSAI-465 follow-up. */}
        <g
          data-testid="org-graph-zoom-wrapper"
          transform={`translate(${transform.x}, ${transform.y}) scale(${transform.k})`}
        >
          <TeamHullsLayer
            teamLeaders={teamLeaders}
            nodes={nodes}
            positions={positions}
            colorForTeamLeader={colorForTeamLeader}
          />
          <EdgeLayer
            edges={edges}
            positions={positions}
            nodes={nodes}
            colorForNode={colorForNode}
          />
          <NodeLayer
            nodes={nodes}
            positions={positions}
            departmentNameById={departmentNameById}
            focusedNodeId={focusedNodeId}
            colorForNode={colorForNode}
            onNodeClick={handleNodeClick}
            onNodeKeyDown={handleNodeKeyDown}
            onNodeFocus={setFocusedNodeId}
          />
        </g>
      </svg>
      </div>
    </div>
  );
}

interface TeamHullsLayerProps {
  /** Visible nodes that have ≥1 direct report (i.e. team leaders). */
  teamLeaders: IOrgGraphNode[];
  /** Full visible-node set (used to look up each leader's reports). */
  nodes: IOrgGraphNode[];
  positions: Map<string, NodePosition>;
  colorForTeamLeader: (leaderId: string) => string;
}

/**
 * One hull per team leader, enclosing the leader + their LEAF direct
 * reports (reports that don't themselves lead a sub-team). Sub-team
 * leaders get their own hull elsewhere; if we drew them in BOTH their
 * parent's hull AND their own, the hulls overlap heavily at the sub-
 * leader's position — which visually reads as "this is all one big
 * team" and makes the hierarchy unreadable.
 *
 * Trade-off: a manager whose direct reports are ALL sub-team leaders
 * ends up with no leaf members to bubble; we skip their hull entirely
 * in that case (the edges to each sub-leader still show the
 * relationship).
 *
 * See PRODOPSAI-465 thread / bild9 for the screenshot that motivated
 * this filtering.
 */
function TeamHullsLayer({
  teamLeaders,
  nodes,
  positions,
  colorForTeamLeader,
}: Readonly<TeamHullsLayerProps>) {
  const leaderIds = useMemo(
    () => new Set(teamLeaders.map((l) => l.id)),
    [teamLeaders],
  );
  return (
    <g data-testid="team-hulls-layer">
      {teamLeaders.map((leader) => {
        const leafReports = nodes.filter(
          (n) => n.managerUpn === leader.id && !leaderIds.has(n.id),
        );
        if (leafReports.length === 0) {
          // All direct reports lead sub-teams — no leaves to bubble.
          return null;
        }
        const teamMembers = [leader, ...leafReports];
        const points = teamMembers
          .map((m) => positions.get(m.id))
          .filter((p): p is NodePosition => p !== undefined);
        if (points.length === 0) return null;
        return (
          <DepartmentHull
            key={leader.id}
            name={`${leader.displayName}'s team`}
            points={points}
            color={colorForTeamLeader(leader.id)}
          />
        );
      })}
    </g>
  );
}

interface EdgeLayerProps {
  edges: IOrgGraphResponse['edges'];
  positions: Map<string, NodePosition>;
  nodes: IOrgGraphNode[];
  colorForNode: (node: IOrgGraphNode) => string;
}

function EdgeLayer({ edges, positions, nodes, colorForNode }: Readonly<EdgeLayerProps>) {
  const nodeById = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  return (
    <g data-testid="edge-layer">
      {edges.map((edge) => {
        const source = positions.get(edge.source);
        const target = positions.get(edge.target);
        if (!source || !target) return null;
        // Edges out of a ghost manager are dashed and grey to signal the
        // reporting line leaves the P&E org. Real edges are coloured by the
        // report's team (= the team the source manager leads), so every edge
        // in a pod shares the pod's colour and the hierarchy is easy to trace.
        const fromGhost = isGhostId(edge.source);
        const targetNode = nodeById.get(edge.target);
        const targetStroke = targetNode ? colorForNode(targetNode) : 'var(--color-foreground)';
        const stroke = fromGhost ? 'var(--color-muted-foreground)' : targetStroke;
        return (
          <EdgeLine
            key={`${edge.source}->${edge.target}`}
            sourceX={source.x}
            sourceY={source.y}
            targetX={target.x}
            targetY={target.y}
            stroke={stroke}
            dashed={fromGhost}
          />
        );
      })}
    </g>
  );
}

interface NodeLayerProps {
  nodes: IOrgGraphNode[];
  positions: Map<string, NodePosition>;
  departmentNameById: Record<string, string>;
  focusedNodeId: string | null;
  colorForNode: (node: IOrgGraphNode) => string;
  onNodeClick: (nodeId: string) => void;
  onNodeKeyDown: (event: KeyboardEvent<SVGGElement>, nodeId: string) => void;
  onNodeFocus: (nodeId: string) => void;
}

function NodeLayer({
  nodes,
  positions,
  departmentNameById,
  focusedNodeId,
  colorForNode,
  onNodeClick,
  onNodeKeyDown,
  onNodeFocus,
}: Readonly<NodeLayerProps>) {
  return (
    <g data-testid="node-layer">
      {nodes.map((node) => {
        const pos = positions.get(node.id);
        if (!pos) return null;
        // Ghost-manager bridges render as a greyed, non-interactive node:
        // no hover card, no click, not keyboard-reachable.
        if (isGhostId(node.id)) {
          return (
            <GraphGhostNode key={node.id} name={node.displayName} x={pos.x} y={pos.y} />
          );
        }
        const isFocused = focusedNodeId === node.id;
        return (
          <PersonHoverCard
            key={node.id}
            node={node}
            departmentLabel={departmentNameById[node.departmentId] ?? null}
          >
            <g
              role="button"
              tabIndex={0}
              aria-label={`${node.displayName}, ${node.jobTitle ?? 'no title'}, ${
                departmentNameById[node.departmentId] ?? 'no department'
              }`}
              className={isFocused ? 'org-graph-focused-node' : undefined}
              data-testid="org-graph-node"
              data-focused={isFocused ? 'true' : undefined}
              onClick={() => onNodeClick(node.id)}
              onKeyDown={(event) => onNodeKeyDown(event, node.id)}
              onFocus={() => onNodeFocus(node.id)}
            >
              <PersonNode
                node={node}
                x={pos.x}
                y={pos.y}
                color={colorForNode(node)}
                // Labels only when this node is focused (incl. arrow-key
                // navigation focus) — keeps dense graphs readable while
                // letting users surface a specific name. Hover still opens
                // PersonHoverCard with the full details.
                showLabel={isFocused}
              />
            </g>
          </PersonHoverCard>
        );
      })}
    </g>
  );
}

/**
 * Top-level component. Forks on mobile vs desktop so the SVG and its force
 * simulation never mount on narrow viewports.
 */
export function OrgGraph({ data }: Readonly<OrgGraphProps>) {
  const isMobile = useIsMobile();
  if (isMobile) {
    return <MobileListView data={data} />;
  }
  return <DesktopGraph data={data} />;
}

// Exposed for tests that want to assert palette stability without hitting the
// DOM.
export const __testables = {
  buildTeamColorMap,
  teamIdForNode,
  ORPHAN_TEAM_ID,
  ORPHAN_COLOR,
  BREATHING_ALPHA_TARGET,
};
